import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import ImagerApi from "../../api/ImagerApi";
import { Coordinate2D } from "../../Model/Coordinate";
import { AutoFocusCompleted } from "../../Model/Efi";
import { EModuleType } from "../../Model/EModuleType";
import { getModule, getModules } from "../../redux/reducers/ModuleReducer";
import { getAutoFocusById, getModuleState } from "../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import LightPathUtil from "../../Utility/LightPathUtil";
import Breadcrumb from "../Header/Breadcrumb";
import Header from "../Header/Header";
import LayoutContent from "../LayoutContent";
import FocusLineChart from "./FocusLineChart";
import ImageGallery, { GalleryImgSrc } from "./ImageGallery";
import ImagerNavSubMenu from "./ImagerNavSubMenu";

function getFocusScore(autoFocusData: AutoFocusCompleted): Array<Coordinate2D> {
  let result: Array<Coordinate2D> = [];

  for (let i = 0; i < autoFocusData.focusScores.length; i++) {
    result.push({
      x: autoFocusData.focusScores[i].focusScore,
      y: autoFocusData.focusScores[i].focusZ,
    });
  }

  return result;
}

const emptyFocusScoreCoordinate: Array<Coordinate2D> = [];

const emptyFocusGallery: GalleryImgSrc[] = [];

type FocusParams = {
  imagerIdentifier: string;
  lightpath: string;
};

const FocusView = ({ match }: RouteComponentProps<FocusParams>) => {
  const imagerModules = useTypedSelector((state) => getModules(state, EModuleType.IMAGER));
  const imagerIdentifier = match.params.imagerIdentifier || imagerModules[0].identifier;
  const imagerModule = useTypedSelector((state) => getModule(state, imagerIdentifier));
  const autoFocusData = useTypedSelector((state) => getAutoFocusById(state)[imagerIdentifier]);

  const moduleState = useTypedSelector((state) => getModuleState(state, imagerIdentifier));
  const imagerState = moduleState.imager;
  const lightpath = match.params.lightpath ? match.params.lightpath : imagerState ? imagerState.activeLightPath : "-1";

  const [focusImageSrc, setFocusImage] = useState("");
  const [focusGallery, setFocusGallery] = useState(emptyFocusGallery);
  const [selectedFocusIndex, setSelectedFocusIndex] = useState(0);
  const [focusScoreCoordinate, SetFocusScoreCoordinate] = useState(emptyFocusScoreCoordinate);
  const [imgSubtitle, setImgSubtitle] = useState("");

  useEffect(() => {
    async function asyncFunc() {
      // TODO: change index based selection if the API support key
      return Promise.all(
        autoFocusData.imagePaths.map(async (imagePath, index) => ({
          name: "Focus " + (index + 1),
          imageSrc: await ImagerApi.autoFocusImage(imagerIdentifier, imagePath),
        }))
      );
    }

    if (autoFocusData) {
      const focusScore: Array<Coordinate2D> = getFocusScore(autoFocusData);
      SetFocusScoreCoordinate(focusScore);

      asyncFunc().then((focusGallery) => {
        setFocusGallery(focusGallery);
        setSelectedFocusIndex(0);
        setFocusImage(focusGallery[0].imageSrc);
        const maxFocus = Math.max(...focusScore.map((focus) => focus.x));
        const currentFocus = focusScore[0].x;
        const focusPercent = (currentFocus / maxFocus) * 100.0;
        setImgSubtitle(`Focus : ${focusPercent.toFixed(1)}%`);
      });
    }
  }, [imagerIdentifier, autoFocusData]);

  const onChangeSelectedIndex = (index: number) => {
    setSelectedFocusIndex(index);
  };

  useEffect(() => {
    if (focusGallery.length > 0) {
      const imgUrl = focusGallery[selectedFocusIndex].imageSrc;
      setFocusImage(imgUrl);
      const maxFocus = Math.max(...focusScoreCoordinate.map((focus) => focus.x));
      const currentFocus = focusScoreCoordinate[selectedFocusIndex].x;
      const focusPercent = (currentFocus / maxFocus) * 100.0;
      setImgSubtitle(`Focus : ${focusPercent.toFixed(1)}%`);
    }
  }, [selectedFocusIndex, focusGallery, focusScoreCoordinate]);

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "Focus Graph",
                to: "/imager/focus/" + imagerIdentifier + "/" + lightpath,
                disabled: false,
              },
            ]}
          />
          <Breadcrumb
            items={[
              {
                title: imagerModule.name,
                to: "/imager/focus/" + imagerIdentifier + "/" + lightpath,
                disabled: false,
              },
            ]}
          />
          <Breadcrumb
            items={[
              {
                title: LightPathUtil.toName(lightpath),
                to: "/imager/focus/" + imagerIdentifier + "/" + lightpath,
                disabled: false,
              },
            ]}
          />
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <ImagerNavSubMenu imagerIdentifier={imagerIdentifier} />
      </LayoutContent.SubMenu>
      <LayoutContent.Content>
        <div
          style={{
            display: "flex",
            height: "100%",
          }}
        >
          <FocusLineChart
            focusScoreCoordinate={focusScoreCoordinate}
            onClickFocus={onChangeSelectedIndex}
            selectedIndex={selectedFocusIndex}
            style={{
              flex: "1 0 auto",
              width: "35%",
              height: "540px",
              margin: "auto",
              paddingRight: 5,
            }}
          />
          <div
            style={{
              flex: "8 0 auto",
              width: "65%",
              display: "block",
              margin: "auto",
              paddingRight: 10,
            }}
          >
            <img
              style={{
                margin: "auto",
                maxWidth: "100%",
                maxHeight: "100%",
              }}
              src={focusImageSrc}
              alt={""}
            />
            <div
              style={{
                marginTop: 20,
              }}
            >
              {imgSubtitle}
            </div>
          </div>
        </div>
      </LayoutContent.Content>
      <LayoutContent.CommandPanel>
        <ImageGallery
          title="Focus Level"
          items={focusGallery}
          selectedIndex={selectedFocusIndex}
          onClickItem={onChangeSelectedIndex}
        />
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
};

export default FocusView;
